import {
  PopupWithApi,
  PopupWithApiItem,
  toast,
  usePortal,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import {
  BIOSIMILAR_SELECT_ALL_ITEM_ID,
  onBiosimilarRetryMarkSuccess,
} from "../actionHandlers/onBiosimilarRetryMarkSuccess";
import {
  getBiosimilarPopupGroupLabel,
  getBiosimilarPopupItemsForDrug,
} from "../utils/biosimilarDrugOptions";

interface BiosimilarRetryPopupProps {
  rowData: OncologySubmissionTableDataModel;
  onClose: () => void;
  onSuccess: () => void;
}

/** "Select All" row prepended to biosimilar drug options in the retry popup. */
const BIOSIMILAR_SELECT_ALL_ITEM: PopupWithApiItem = {
  id: BIOSIMILAR_SELECT_ALL_ITEM_ID,
  label: "Select All",
};

/**
 * Builds a checked map with every drug option selected.
 *
 * @param drugItems - Biosimilar drug checkbox items (excludes Select All)
 * @returns Checkbox state with Select All and all drug options checked
 */
const buildAllDrugOptionsChecked = (
  drugItems: PopupWithApiItem[],
): Record<string, boolean> => {
  const next: Record<string, boolean> = {
    [BIOSIMILAR_SELECT_ALL_ITEM_ID]: true,
  };
  for (const item of drugItems) {
    next[item.id] = true;
  }
  return next;
};

/**
 * Portal overlay that renders Oasis `PopupWithApi` with biosimilar options for the row drug.
 */
const BiosimilarRetryPopup = ({
  rowData,
  onClose,
  onSuccess,
}: BiosimilarRetryPopupProps) => {
  const portal = usePortal();
  const [checkedById, setCheckedById] = useState<Record<string, boolean>>({});

  const drugPopupItems = useMemo(
    () => getBiosimilarPopupItemsForDrug(rowData.drug),
    [rowData.drug],
  );

  const popupItems = useMemo(
    () => [BIOSIMILAR_SELECT_ALL_ITEM, ...drugPopupItems],
    [drugPopupItems],
  );

  const popupGroupLabel = useMemo(
    () => getBiosimilarPopupGroupLabel(rowData.drug),
    [rowData.drug],
  );

  const handleCheckedChange = useCallback(
    (next: Record<string, boolean>) => {
      const selectAllWasChecked = Boolean(
        checkedById[BIOSIMILAR_SELECT_ALL_ITEM_ID],
      );
      const selectAllIsChecked = Boolean(next[BIOSIMILAR_SELECT_ALL_ITEM_ID]);
      const selectAllToggled = selectAllWasChecked !== selectAllIsChecked;

      if (selectAllToggled && selectAllIsChecked) {
        setCheckedById(buildAllDrugOptionsChecked(drugPopupItems));
        return;
      }

      if (selectAllToggled && !selectAllIsChecked) {
        setCheckedById({});
        return;
      }

      const allDrugOptionsSelected = drugPopupItems.every(
        (item) => next[item.id],
      );
      setCheckedById({
        ...next,
        [BIOSIMILAR_SELECT_ALL_ITEM_ID]: allDrugOptionsSelected,
      });
    },
    [checkedById, drugPopupItems],
  );

  const handleMarkAsSuccess = useCallback(async () => {
    await onBiosimilarRetryMarkSuccess({
      rowData,
      popupItems: drugPopupItems,
      checkedById,
      onClose,
      onSuccess: () => {
        onSuccess();
        onClose();
      },
    });
  }, [checkedById, drugPopupItems, onClose, onSuccess, rowData]);

  useEffect(() => {
    if (drugPopupItems.length === 0) {
      toast.error(
        `No biosimilar options found for "${rowData.drug ?? "this drug"}".`,
      );
      onClose();
    }
  }, [drugPopupItems.length, onClose, rowData.drug]);

  if (drugPopupItems.length === 0) {
    return null;
  }

  return portal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Biosimilar retry"
      >
        <PopupWithApi
          items={popupItems}
          checkedById={checkedById}
          onCheckedChange={handleCheckedChange}
          onCancel={onClose}
          onMarkAsSuccess={handleMarkAsSuccess}
          checkboxesGroupLabel={popupGroupLabel}
          markSuccessLabel="Initiate"
        />
      </div>
    </div>,
  );
};

export default BiosimilarRetryPopup;
